import propz from 'propz';

export const programmeBookingPad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

export const programmeBookingFormatCountdown = (totalSec: number) => {
  const s = Math.max(0, Math.floor(totalSec));
  const dd = Math.floor(s / 86400);
  const hh = Math.floor((s % 86400) / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;

  if (dd > 0) return `${dd}d ${programmeBookingPad2(hh)}:${programmeBookingPad2(mm)}:${programmeBookingPad2(ss)}`;
  return `${programmeBookingPad2(hh)}:${programmeBookingPad2(mm)}:${programmeBookingPad2(ss)}`;
};

export const programmeBookingToMs = (d: any): number | null => {
  if (!d) return null;
  const t = new Date(d).getTime();
  return Number.isFinite(t) ? t : null;
};

export const programmeBookingWeekdayOrderKey = (day: string) => {
  const map: Record<string, number> = { MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6, SUN: 7 };
  return map[String(day || '').toUpperCase()] || 99;
};

export const programmeBookingFormatTimeRange = (schedule: any) => {
  const startTime = propz.get(schedule, ['startTime'], '');
  const endTime = propz.get(schedule, ['endTime'], '');
  if (startTime && endTime) return `${startTime}–${endTime}`;
  if (startTime) return `${startTime}`;

  const timeIso = propz.get(schedule, ['time'], null);
  if (!timeIso) return '';

  const start = new Date(timeIso);
  if (!Number.isFinite(start.getTime())) return '';

  const durMinRaw = propz.get(schedule, ['duration'], null);
  const durMin = typeof durMinRaw === 'number' ? durMinRaw : parseInt(String(durMinRaw || ''), 10);
  const hasDur = Number.isFinite(durMin) && durMin > 0;

  const fmt = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (!hasDur) return fmt(start);

  const end = new Date(start.getTime() + durMin * 60 * 1000);
  return `${fmt(start)}–${fmt(end)}`;
};

export const programmeBookingFormatDateRange = (schedule: any) => {
  const startIso = propz.get(schedule, ['startDate'], null);
  const finishIso = propz.get(schedule, ['finishDate'], null);

  const toDate = (x: any) => {
    const d = x ? new Date(x) : null;
    return d && Number.isFinite(d.getTime()) ? d : null;
  };

  const sd = toDate(startIso);
  const fd = toDate(finishIso);

  if (!sd && !fd) return '';

  const fmt = (d: Date) => d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });

  if (sd && fd) return `${fmt(sd)} – ${fmt(fd)}`;
  if (sd) return `From ${fmt(sd)}`;
  return `Until ${fmt(fd!)}`;
};

export const programmeBookingErrData = (e: any) => propz.get(e, ['response', 'data'], propz.get(e, ['data'], null));

export const programmeBookingErrCode = (e: any): string | null => {
  const d = programmeBookingErrData(e);
  const c = propz.get(d, ['code'], propz.get(d, ['errorCode'], null));
  return c ? String(c) : null;
};

export const programmeBookingErrMessage = (e: any, fallback: string = 'Request failed') => {
  const d = programmeBookingErrData(e);
  return String(propz.get(d, ['error'], propz.get(d, ['message'], propz.get(e, ['message'], fallback)))) || fallback;
};

export const programmeBookingThrowIfOkError = (resp: any) => {
  if (resp && (resp.error || resp.code)) throw { response: { data: resp } };
  return resp;
};

export const programmeBookingFieldKey = (f: any) =>
  String(propz.get(f, ['_id'], propz.get(f, ['id'], propz.get(f, ['heading'], 'field'))));

export const programmeBookingIsFieldRequired = (f: any) => !!propz.get(f, ['isRequired'], false);

export const programmeBookingFieldType = (f: any) => String(propz.get(f, ['type'], 'STRING') || 'STRING').toUpperCase();

export const programmeBookingValidateAnswers = (fields: any[], ans: Record<string, any>) => {
  const errors: string[] = [];

  (fields || []).forEach(f => {
    const key = programmeBookingFieldKey(f);
    if (!programmeBookingIsFieldRequired(f)) return;

    const t = programmeBookingFieldType(f);
    const v = ans[key];

    if (t === 'BOOLEAN') {
      if (typeof v !== 'boolean') errors.push(`Please answer: ${propz.get(f, ['heading'], 'Question')}`);
      return;
    }

    if (t === 'NUMBER') {
      if (v === '' || v === null || typeof v === 'undefined') {
        errors.push(`Please answer: ${propz.get(f, ['heading'], 'Question')}`);
        return;
      }
      const n = typeof v === 'number' ? v : Number(String(v));
      if (!Number.isFinite(n)) errors.push(`Please enter a valid number: ${propz.get(f, ['heading'], 'Question')}`);
      return;
    }

    if (String(v || '').trim() === '') errors.push(`Please answer: ${propz.get(f, ['heading'], 'Question')}`);
  });

  return errors;
};

export const programmeBookingBuildAnsweredFields = (fields: any[], ans: Record<string, any>) => {
  return (fields || []).map(f => {
    const key = programmeBookingFieldKey(f);
    const t = programmeBookingFieldType(f);
    const raw = ans[key];

    let value: any = raw;

    if (t === 'BOOLEAN') {
      value = raw === true ? true : raw === false ? false : undefined;
    } else if (t === 'NUMBER') {
      if (raw === '' || raw === null || typeof raw === 'undefined') value = undefined;
      else {
        const n = typeof raw === 'number' ? raw : Number(String(raw));
        value = Number.isFinite(n) ? n : undefined;
      }
    } else {
      if (typeof raw === 'undefined') value = undefined;
      else {
        const s = String(raw);
        value = s.trim() ? s : undefined;
      }
    }

    return { ...f, value };
  });
};

export const programmeBookingBuildInitialAnswersFromFields = (fields: any[]): Record<string, any> => {
  const out: Record<string, any> = {};

  (fields || []).forEach(f => {
    const key = programmeBookingFieldKey(f);
    const t = programmeBookingFieldType(f);
    const v = propz.get(f, ['value'], undefined);

    if (typeof v === 'undefined') return;

    if (t === 'BOOLEAN') out[key] = v === true ? true : v === false ? false : undefined;
    else if (t === 'NUMBER') out[key] = v;
    else out[key] = String(v);
  });

  return out;
};

export const programmeBookingHasAnyAnsweredValue = (fields: any[]): boolean => {
  return (fields || []).some(f => {
    const v = propz.get(f, ['value'], undefined);
    if (typeof v === 'boolean') return true;
    if (typeof v === 'number') return Number.isFinite(v);
    if (typeof v === 'string') return v.trim().length > 0;
    return v !== null && typeof v !== 'undefined';
  });
};

export const programmeBookingBuildAnswersFromInviteFields = (fields: any[]): Record<string, any> => {
  const out: Record<string, any> = {};

  (fields || []).forEach(f => {
    const key = programmeBookingFieldKey(f);
    const t = programmeBookingFieldType(f);
    const v = propz.get(f, ['value'], undefined);

    if (typeof v === 'undefined' || v === null) return;

    if (t === 'BOOLEAN') {
      if (typeof v === 'boolean') out[key] = v;
      return;
    }

    if (t === 'NUMBER') {
      if (typeof v === 'number') out[key] = v;
      else {
        const n = Number(String(v));
        if (Number.isFinite(n)) out[key] = n;
      }
      return;
    }

    out[key] = String(v);
  });

  return out;
};

export const programmeBookingGetInviteStatus = (row: any) =>
  String(propz.get(row, ['invite', 'invitationStatus'], '') || '').toUpperCase();

/**
 * Status groups used by booking UI.
 * Active day occupancy means: this selection still "owns" a slot in the day.
 * Remove-pending does NOT own a slot anymore for next-choice purposes.
 */
export const programmeBookingIsDraftLikeStatus = (status: any): boolean => {
  const s = String(status || '').toUpperCase();
  return s === 'PROGRAMME_DRAFT' || s === 'PROGRAMME_DRAFT_UPDATE' || s === 'PROGRAMME_DRAFT_REMOVE';
};

export const programmeBookingIsDayOccupyingStatus = (status: any): boolean => {
  const s = String(status || '').toUpperCase();
  return s === 'PROGRAMME_DRAFT' || s === 'PROGRAMME_DRAFT_UPDATE' || s === 'ACCEPTED';
};

export const programmeBookingIsPriorityOccupyingRow = (row: any): boolean => {
  const status = programmeBookingGetInviteStatus(row);
  return programmeBookingIsDayOccupyingStatus(status);
};

export const programmeBookingGetRowPriority = (row: any): number | null => {
  const raw = propz.get(row, ['invite', 'priority'], undefined);
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return Number.isFinite(n) ? n : null;
};

export const programmeBookingFindExistingDraftInSameDay = (dayRow: any, exceptClubId: string): any | null => {
  const clubs = (propz.get(dayRow, ['clubs'], []) || []) as any[];

  for (const r of clubs) {
    const cid = String(propz.get(r, ['clubId'], ''));
    if (!cid || cid === exceptClubId) continue;

    const status = programmeBookingGetInviteStatus(r);
    if (programmeBookingIsDayOccupyingStatus(status)) return r;
  }

  return null;
};

export const programmeBookingFindPriorityConflictInDay = (
  dayRow: any,
  exceptClubId: string,
  priority: number
): any | null => {
  const clubs = (propz.get(dayRow, ['clubs'], []) || []) as any[];

  for (const r of clubs) {
    const cid = String(propz.get(r, ['clubId'], ''));
    if (!cid || cid === exceptClubId) continue;
    if (!programmeBookingIsPriorityOccupyingRow(r)) continue;

    const pr = programmeBookingGetRowPriority(r);
    if (pr !== null && pr === priority) return r;
  }

  return null;
};

export const programmeBookingGetUsedPrioritiesInDay = (dayRow: any, exceptClubId?: string): number[] => {
  const clubs = (propz.get(dayRow, ['clubs'], []) || []) as any[];

  const used = clubs
    .filter(r => {
      const cid = String(propz.get(r, ['clubId'], ''));
      if (!cid) return false;
      if (exceptClubId && cid === exceptClubId) return false;
      return programmeBookingIsPriorityOccupyingRow(r);
    })
    .map(r => programmeBookingGetRowPriority(r))
    .filter((n): n is number => typeof n === 'number' && Number.isFinite(n));

  return Array.from(new Set(used)).sort((a, b) => a - b);
};

export const programmeBookingGetNextAvailablePriorityInDay = (
  dayRow: any,
  minPriority: number,
  maxPriority: number,
  exceptClubId?: string
): number | null => {
  const used = new Set(programmeBookingGetUsedPrioritiesInDay(dayRow, exceptClubId));

  for (let i = minPriority; i <= maxPriority; i += 1) {
    if (!used.has(i)) return i;
  }

  return null;
};

export const programmeBookingPickAnyPlayerDetails = (data: any) => {
  const days = (propz.get(data, ['days'], []) || []) as any[];
  for (const d of days) {
    const clubs = (propz.get(d, ['clubs'], []) || []) as any[];
    for (const r of clubs) {
      const pd = propz.get(r, ['invite', 'playerDetails'], null);
      const userId = String(propz.get(pd, ['userId'], '') || '');
      const permissionId = String(propz.get(pd, ['permissionId'], '') || '');
      if (userId && permissionId) return { userId, permissionId };
    }
  }
  return null;
};

export const programmeBookingFindClubNameByClubId = (days: any[], clubId: string): string | null => {
  const all = (days || []).reduce((acc: any[], d: any) => acc.concat((propz.get(d, ['clubs'], []) || []) as any[]), []);
  const row = all.find(r => String(propz.get(r, ['clubId'], '')) === String(clubId));
  if (!row) return null;
  return String(propz.get(row, ['club', 'name'], '')) || null;
};

export const programmeBookingFormatError = (e: any, days: any[]) => {
  const d = programmeBookingErrData(e) || {};
  const code = programmeBookingErrCode(e);
  const baseMsg = programmeBookingErrMessage(e);

  const conflictClubId = String(propz.get(d, ['conflictClubId'], '') || '');
  const conflictDay = String(propz.get(d, ['conflictDay'], propz.get(d, ['day'], '')) || '');
  const conflictClubNameFromApi = String(propz.get(d, ['conflictClubName'], '') || '').trim();
  const conflictName =
    conflictClubNameFromApi || (conflictClubId ? programmeBookingFindClubNameByClubId(days, conflictClubId) : null);

  const conflictRole = String(propz.get(d, ['conflictInvitationRole'], '') || '').toUpperCase();

  const roleText =
    conflictRole === 'PARENT'
      ? 'another parent user'
      : conflictRole === 'STUDENT'
      ? 'another student user'
      : 'another user';

  if (code === 'PRIORITY_ALREADY_USED') {
    const pr = propz.get(d, ['priority'], undefined);
    const prTxt = typeof pr === 'number' ? `P${pr}` : 'This priority';
    const clubTxt = conflictName ? `"${conflictName}"` : 'another club';
    const dayTxt = conflictDay ? ` on ${conflictDay}` : '';

    if (baseMsg.toLowerCase().startsWith('you already use')) {
      return `${prTxt} is already used for ${clubTxt}${dayTxt}. Please choose a different priority or update that selection first.`;
    }

    return `${prTxt} is already used by ${roleText} for ${clubTxt}${dayTxt}. Please choose a different priority or change that selection first.`;
  }

  if (code === 'DAY_ALREADY_HAS_SELECTION') {
    const clubTxt = conflictName ? `"${conflictName}"` : 'another club';
    const dayTxt = conflictDay ? ` on ${conflictDay}` : '';

    if (baseMsg.toLowerCase().startsWith('you already selected')) {
      return `You already selected ${clubTxt}${dayTxt}. Only one club can be selected per day. Please change or remove that selection first.`;
    }

    return `${clubTxt}${dayTxt} is already selected by ${roleText}. Only one club can be selected per day. Please change that selection first.`;
  }

  if (code === 'PRIORITY_REQUIRED') return 'Please select a priority for this club.';
  if (code === 'PRIORITY_OUT_OF_RANGE') return baseMsg;

  return baseMsg;
};

export const programmeBookingStatusMeta = (stRaw: string) => {
  const st = String(stRaw || '').toUpperCase();

  if (st === 'ACCEPTED') return { text: 'Submitted to school', cls: 'badge badge-success' };
  if (st === 'PROGRAMME_DRAFT') return { text: 'Draft', cls: 'badge badge-info' };
  if (st === 'PROGRAMME_DRAFT_UPDATE') return { text: 'Updated draft', cls: 'badge badge-info' };
  if (st === 'PROGRAMME_DRAFT_REMOVE') return { text: 'Pending removal', cls: 'badge badge-warning' };
  if (st === 'PENDING') return { text: 'Available for booking', cls: 'badge badge-secondary' };

  return { text: st || '—', cls: 'badge badge-light border' };
};

export const programmeBookingFormatClubAges = (club: any, user: any, getAgesGroupFormattedFn: any) => {
  const ages = (propz.get(club, ['ages'], []) || []) as number[];
  return getAgesGroupFormattedFn({ ages }, { user });
};

